import React, { Ref, forwardRef, useCallback } from 'react';

import { ListRenderItemInfo, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { BottomSheetModal } from '@gorhom/bottom-sheet';

import IconArrowDownSmall from 'assets/images/svgs/ico_arrow_down_small.svg';
import IconArrowUpSmall from 'assets/images/svgs/ico_arrow_up_small.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { EPG_DATE_PICKER_BUTTON_ID } from 'config/testID';

import { isPhone, isTablet, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import { DateItem, Position } from 'screens/Epg/hooks/useDatePicker';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetList from 'components/BottomSheetList';
import PickerList from 'components/PickerList';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import BottomSheetDatePickerRowItem from './BottomSheetDatePickerRowItem';
import rowStyles from './BottomSheetDatePickerRowItem/styles';
import DatePickerRowItem from './DatePickerRowItem';
import rowPickerStyles from './DatePickerRowItem/styles';
import styles from './styles';

type Props = {
    handleOpen: () => void;
    selectedDateLabel: string;
    data: DateItem[];
    onClose: () => void;
    isModalVisible: boolean;
    modalStartingPosition?: Position;
    bottomSheetModalRef?: Ref<BottomSheetModal | undefined>;
    screenReaderEnabled: boolean;
    onPressDatePickerItem: (item: DateItem) => void;
    onDismiss(): void;
    isListLayout?: boolean;
};

const transformDateLabel = (dateLabel: string) => dateLabel.replace(', ', ', ');
const PICKER_WIDTH = 164;
export const PICKER_HEIGHT = 192;
export const PICKER_MARGIN = 16;

const {
    ACCESSIBILITY: {
        EPG: { ACCESSIBILITY_EPG_DAY_SELECTOR },
    },
} = LANGUAGE_KEYS;

const EpgGridDatePicker = forwardRef<View, Props>(
    (
        {
            handleOpen,
            selectedDateLabel,
            data,
            onClose,
            isModalVisible,
            modalStartingPosition,
            bottomSheetModalRef,
            screenReaderEnabled,
            onPressDatePickerItem,
            onDismiss,
            isListLayout = false,
        },
        forwardedRef,
    ) => {
        const { t } = useTranslation();

        const renderPickerListDatePickerRow = useCallback(
            ({ item }: ListRenderItemInfo<DateItem>) => (
                <DatePickerRowItem
                    item={item}
                    buttonWidth={PICKER_WIDTH}
                    handleClose={onClose}
                    onPressDatePickerItem={onPressDatePickerItem}
                    testID={item.testID}
                    screenReaderEnabled={screenReaderEnabled}
                />
            ),
            [onClose, onPressDatePickerItem, screenReaderEnabled],
        );

        const renderBottomSheetDatePickerRow = useCallback(
            ({ item }: ListRenderItemInfo<DateItem>) => (
                <BottomSheetDatePickerRowItem
                    item={item}
                    onPressDatePickerItem={onPressDatePickerItem}
                    handleClose={onClose}
                    testID={item.testID}
                />
            ),
            [onPressDatePickerItem, onClose, screenReaderEnabled],
        );

        return (
            <>
                <View style={styles.datePickerContainer}>
                    <Pressable
                        ref={forwardedRef}
                        testID={EPG_DATE_PICKER_BUTTON_ID}
                        style={styles.datePickerButtonContainer}
                        onPress={handleOpen}
                        dataSet={{ class: 'epgDatePicker' }}
                        accessible
                        accessibilityRole="button"
                        accessibilityLabel={`${t(
                            ACCESSIBILITY_EPG_DAY_SELECTOR,
                        )}. ${transformDateLabel(selectedDateLabel)}`}
                    >
                        <Text
                            accessible={false}
                            category={ETextCategory.caption1}
                            color={ETextColor.neutral}
                            bold
                        >
                            {transformDateLabel(selectedDateLabel)}
                        </Text>
                        <View
                            style={[
                                styles.svgArrowContainer,
                                isListLayout && styles.svgArrowListContainer,
                            ]}
                            accessible={false}
                        >
                            {isModalVisible && (isTablet || isWeb) ? (
                                <IconArrowUpSmall />
                            ) : (
                                <IconArrowDownSmall />
                            )}
                        </View>
                    </Pressable>
                </View>
                {isTabletOrWeb && (
                    <PickerList
                        data={data}
                        isModalVisible={isModalVisible}
                        modalStartingPosition={modalStartingPosition}
                        listWidth={PICKER_WIDTH}
                        listHeight={PICKER_HEIGHT}
                        rowHeight={rowPickerStyles.container.height}
                        renderItem={renderPickerListDatePickerRow}
                        keyExtractor={keyExtractor}
                        onClose={onClose}
                    />
                )}
                {isPhone && !screenReaderEnabled && (
                    <BottomSheetList
                        ref={bottomSheetModalRef}
                        data={data}
                        headerTitle={t(LANGUAGE_KEYS.DATEPICKER.TITLE)}
                        keyExtractor={keyExtractor}
                        rowHeight={rowStyles.container.height}
                        renderRowItem={renderBottomSheetDatePickerRow}
                        onClose={onClose}
                        onDismiss={onDismiss}
                    />
                )}
                {isPhone && screenReaderEnabled && (
                    <BottomSheetAccessibleList
                        visible={isModalVisible}
                        data={data}
                        headerTitle={t(LANGUAGE_KEYS.DATEPICKER.TITLE)}
                        keyExtractor={keyExtractor}
                        renderRowItem={renderBottomSheetDatePickerRow}
                        onClose={onClose}
                    />
                )}
            </>
        );
    },
);

export default EpgGridDatePicker;

function keyExtractor({ label }: DateItem) {
    return label;
}
